import React, { useEffect, useState, useMemo } from "react";
import { Card, Table, Modal, Button, Row, Col } from "react-bootstrap";
import {
  FiMaximize2,
  FiX,
  FiBox,
  FiLayers,
  FiCalendar,
  FiUser,
  FiClipboard,
  FiArrowUp,
  FiArrowDown,
  FiAlertTriangle,
  FiInfo,
} from "react-icons/fi";

const DEPOSITO_PRINCIPALE = "04";

const CruscottoWebTabella = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "Articolo",
    direction: "ascending",
  });

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => {
        if (!res.ok) throw new Error(`Errore HTTP! Stato: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        setData(json?.CRUSCOTTO_WEB || []);
        loading(false);
      })
      .catch((err) => {
        console.error("Errore caricamento cruscotto:", err);
        setLoading(false);
      });
  }, []);

  const formatData = (dateStr) => {
    if (!dateStr) return "-";
    const pureDate = dateStr.split(" ")[0];
    const parts = pureDate.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  const formatQta = (qtaStr) => Math.floor(parseFloat(qtaStr)) || 0;

  const apriDettaglio = (item, e) => {
    e.stopPropagation();
    setSelectedItem(item);
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return null;
    return sortConfig.direction === "ascending" ? (
      <FiArrowUp className="ms-1" />
    ) : (
      <FiArrowDown className="ms-1" />
    );
  };

  const variantiArticoloSelezionato = useMemo(() => {
    if (!selectedItem) return [];
    return data.filter(
      (item) =>
        item.Articolo === selectedItem.Articolo &&
        item.Prec === selectedItem.Prec &&
        item["Num Ord"] === selectedItem["Num Ord"],
    );
  }, [selectedItem, data]);

  const depositiConGiacenzaAltrove = useMemo(() => {
    return variantiArticoloSelezionato.filter(
      (v) => v.DEP2 !== DEPOSITO_PRINCIPALE && formatQta(v.Giacenza2) > 0,
    );
  }, [variantiArticoloSelezionato]);

  const haGiacenzaAltrove = depositiConGiacenzaAltrove.length > 0;

  const processedData = useMemo(() => {
    let items = data.filter(
      (item) => item.DEP2 === DEPOSITO_PRINCIPALE && formatQta(item.Qta) > 0,
    );

    if (sortConfig.key !== null) {
      items.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (
          ["Qta", "Qta Evasa", "Qta Da Evadere", "Giacenza2"].includes(
            sortConfig.key,
          )
        ) {
          aValue = formatQta(aValue);
          bValue = formatQta(bValue);
        } else {
          aValue = (aValue || "").toString().toLowerCase();
          bValue = (bValue || "").toString().toLowerCase();
        }

        if (aValue < bValue)
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (aValue > bValue)
          return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [data, sortConfig]);

  const RenderTabella = ({ isFullscreen = false }) => (
    <div
      className="custom-scroll flex-grow-1"
      style={{
        // FIX: Se non è in fullscreen l'altezza massima viene bloccata a 280px per stare nei 350px della card
        height: isFullscreen ? "75vh" : "280px",
        overflowY: "auto",
        overflowX: "auto",
      }}
    >
      <Table
        hover
        className="align-middle mb-0 custom-table"
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <thead className="bg-light sticky-top" style={{ zIndex: 10, top: 0 }}>
          <tr
            className="text-muted text-uppercase style-header-row"
            style={{
              fontSize: "0.68rem",
              fontWeight: "700",
              letterSpacing: "0.5px",
            }}
          >
            <th
              onClick={() => requestSort("Prec")}
              style={{ cursor: "pointer", width: "60px" }}
            >
              Pre. {getSortIcon("Prec")}
            </th>
            <th
              onClick={() => requestSort("Articolo")}
              style={{ cursor: "pointer", width: "120px" }}
            >
              Articolo {getSortIcon("Articolo")}
            </th>
            <th
              onClick={() => requestSort("Descrizione")}
              style={{ cursor: "pointer", width: "auto" }}
            >
              Cliente {getSortIcon("Descrizione")}
            </th>
            <th
              onClick={() => requestSort("Qta")}
              className="text-center"
              style={{ cursor: "pointer", width: "65px" }}
            >
              Q.tà {getSortIcon("Qta")}
            </th>
            <th
              onClick={() => requestSort("Qta Evasa")}
              className="text-center"
              style={{ cursor: "pointer", width: "65px" }}
            >
              Evasa {getSortIcon("Qta Evasa")}
            </th>
            <th
              onClick={() => requestSort("Qta Da Evadere")}
              className="text-center"
              style={{ cursor: "pointer", width: "75px" }}
            >
              Da Ev. {getSortIcon("Qta Da Evadere")}
            </th>
            <th
              onClick={() => requestSort("Data Ord")}
              style={{ cursor: "pointer", width: "95px" }}
            >
              Data Ord. {getSortIcon("Data Ord")}
            </th>
            <th
              onClick={() => requestSort("Num Ord")}
              style={{ cursor: "pointer", width: "85px" }}
            >
              Num. Ord. {getSortIcon("Num Ord")}
            </th>
            <th
              onClick={() => requestSort("Tipo Ord")}
              style={{ cursor: "pointer", width: "70px" }}
            >
              Tipo {getSortIcon("Tipo Ord")}
            </th>
            <th
              className="text-center"
              style={{ width: "40px" }}
              title="Giacenza disponibile in altri depositi"
            >
              !
            </th>
          </tr>
        </thead>
        <tbody style={{ fontSize: isFullscreen ? "0.85rem" : "0.75rem" }}>
          {loading ? (
            <tr>
              <td
                colSpan="10"
                className="text-center py-4 text-muted fst-italic"
              >
                Caricamento in corso...
              </td>
            </tr>
          ) : processedData.length > 0 ? (
            processedData.map((item, index) => {
              const altriConGiacenza = data.filter(
                (r) =>
                  r.Articolo === item.Articolo &&
                  r.Prec === item.Prec &&
                  r["Num Ord"] === item["Num Ord"] &&
                  r.DEP2 !== DEPOSITO_PRINCIPALE &&
                  formatQta(r.Giacenza2) > 0,
              );
              const segnala = altriConGiacenza.length > 0;

              return (
                <tr
                  key={index}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => apriDettaglio(item, e)}
                >
                  <td className="text-secondary fw-semibold text-truncate">
                    {item.Prec}
                  </td>
                  <td className="fw-bold text-dark text-truncate">
                    {item.Articolo}
                  </td>
                  <td
                    className="fw-bold text-truncate"
                    title={item["Ragione Sociale"] || "Nessuna info"}
                  >
                    {item["Ragione Sociale"] || "Nessuna info"}
                  </td>{" "}
                  <td className="text-center fw-semibold text-dark text-truncate">
                    {formatQta(item.Qta)}
                  </td>
                  <td className="text-center fw-semibold text-success text-truncate">
                    {formatQta(item["Qta Evasa"])}
                  </td>
                  <td className="text-center fw-semibold text-danger text-truncate">
                    {formatQta(item["Qta Da Evadere"])}
                  </td>
                  <td className="text-dark text-truncate">
                    {formatData(item["Data Ord"])}
                  </td>
                  <td className="fw-semibold text-dark text-truncate">
                    {item["Num Ord"]}
                  </td>
                  <td className="text-secondary fw-medium text-truncate">
                    {item["Tipo Ord"]}
                  </td>
                  <td className="text-center">
                    {segnala && (
                      <span
                        className="blink-dot"
                        title={`Giacenza disponibile nei depositi: ${altriConGiacenza
                          .map((r) => r.DEP2)
                          .join(", ")}`}
                      />
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan="10"
                className="text-center py-4 text-muted fst-italic"
              >
                Nessun articolo in elenco per il deposito {DEPOSITO_PRINCIPALE}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <Card
      className="border-0 w-100 d-flex flex-column"
      // FIX: Aggiunto display flex e overflow hidden per assicurare che nulla esca dai 350px totali
      style={{
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        maxHeight: "400px",
        overflow: "hidden",
      }}
    >
      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 10px; }
        .custom-table { table-layout: fixed; width: 100%; }
        .custom-table th { background: #f8f9fa; border-bottom: 2px solid #dee2e6 !important; user-select: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .custom-table th:hover { background: #f1f5f9; }
        .custom-table td { border-bottom: 1px solid rgba(0,0,0,0.04); padding: 10px 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .style-header-row th { vertical-align: middle; }

        .blink-dot {
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #dc3545;
          box-shadow: 0 0 0 rgba(220, 53, 69, 0.6);
          animation: blink-pulse 1.4s infinite;
        }
        @keyframes blink-pulse {
          0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.6); opacity: 1; }
          70% { box-shadow: 0 0 0 7px rgba(220, 53, 69, 0); opacity: 0.85; }
          100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); opacity: 1; }
        }

        .dep-row-alert { background-color: rgba(220, 53, 69, 0.06); }

        .cruscotto-info-btn {
          width: 27px;
          height: 27px;
          border: 1px solid #dfe3e8;
          border-radius: 50%;
          background: #fff;
          color: #0d6efd;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: all 0.15s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          cursor: pointer;
        }
        .cruscotto-info-btn:hover {
          background: #e7f1ff;
          border-color: #b6d4fe;
          transform: translateY(-1px);
        }
        .cruscotto-info-backdrop { z-index: 20990 !important; }
        .cruscotto-info-card {
          border: 1px solid #e9ecef;
          border-radius: 14px;
          background: #fff;
          padding: 16px;
          height: 100%;
        }
        .cruscotto-info-step {
          width: 28px;
          height: 28px;
          min-width: 28px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.72rem;
          font-weight: 800;
          background: #e7f1ff;
          color: #0d6efd;
        }
        .cruscotto-info-list {
          margin: 0;
          padding-left: 18px;
          color: #495057;
          font-size: 0.82rem;
        }
        .cruscotto-info-list li + li { margin-top: 7px; }
      `}</style>
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div className="d-flex align-items-center">
          <div
            className="d-flex align-items-center"
            onClick={() => setShowFullscreenModal(true)}
            style={{ cursor: "pointer" }}
          >
            <span
              style={{
                fontWeight: "800",
                fontSize: "0.75rem",
                color: "#1a1a1a",
                letterSpacing: "1px",
                marginRight: "8px",
              }}
            >
              CRUSCOTTO WEB - DEPOSITO {DEPOSITO_PRINCIPALE}
            </span>
            <FiMaximize2 size={12} color="#adb5bd" />
          </div>

          <button
            type="button"
            className="cruscotto-info-btn ms-2"
            onClick={(e) => {
              e.stopPropagation();
              setShowInfoModal(true);
            }}
            aria-label="Informazioni sul Cruscotto Web"
          >
            <FiInfo size={14} />
          </button>
        </div>
        {!loading && (
          <div
            className="text-muted small fw-bold"
            style={{ fontSize: "0.7rem" }}
          >
            {processedData.length} RIGHE
          </div>
        )}
      </div>
      {/* FIX: Rimosso classi inutili e agganciato flex-grow-1 con overflow hidden */}
      <Card.Body className="p-0 flex-grow-1" style={{ overflow: "hidden" }}>
        <RenderTabella isFullscreen={false} />
      </Card.Body>
      {/* MODALE INFORMAZIONI */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        size="lg"
        scrollable
        className="p-5"
        style={{ zIndex: 21000 }}
        backdropClassName="cruscotto-info-backdrop"
      >
        <Modal.Header closeButton className="border-bottom bg-white px-4 py-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 text-primary flex-shrink-0"
              style={{ width: "40px", height: "40px" }}
            >
              <FiInfo size={20} />
            </div>
            <div>
              <Modal.Title className="fw-bold" style={{ fontSize: "1.05rem" }}>
                Come usare il Cruscotto Web - Deposito {DEPOSITO_PRINCIPALE}
              </Modal.Title>
              <div className="text-muted mt-1" style={{ fontSize: "0.78rem" }}>
                Guida alla lettura degli ordini cliente, delle quantità e della
                disponibilità articolo sui diversi depositi.
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          <div className="bg-primary bg-opacity-10 border border-primary border-opacity-10 rounded-4 p-3 mb-3">
            <div className="fw-bold text-primary mb-1">A cosa serve</div>
            <div
              className="text-dark"
              style={{ fontSize: "0.84rem", lineHeight: 1.55 }}
            >
              Il cruscotto evidenzia gli articoli presenti negli ordini cliente
              collegati al deposito principale <strong>{DEPOSITO_PRINCIPALE}</strong>.
              La vista consente di controllare rapidamente quantità richiesta,
              quantità già evasa, quantità ancora da evadere e disponibilità
              dell'articolo negli altri depositi aziendali.
            </div>
          </div>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cruscotto-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cruscotto-info-step">1</span>
                  <div>
                    <div className="fw-bold text-dark">Quali righe vengono mostrate</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Filtro del deposito principale
                    </div>
                  </div>
                </div>
                <ul className="cruscotto-info-list">
                  <li>
                    La tabella principale mostra solo le righe relative al deposito{" "}
                    <strong>{DEPOSITO_PRINCIPALE}</strong>.
                  </li>
                  <li>
                    Vengono considerate soltanto le righe con{" "}
                    <strong>quantità ordine maggiore di zero</strong>.
                  </li>
                  <li>
                    Le informazioni degli altri depositi non vengono perse: vengono
                    utilizzate per segnalare eventuale stock disponibile altrove e
                    sono visibili nel dettaglio articolo.
                  </li>
                </ul>
              </div>
            </Col>

            <Col md={6}>
              <div className="cruscotto-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cruscotto-info-step">2</span>
                  <div>
                    <div className="fw-bold text-dark">Come leggere la tabella</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Informazioni principali della riga
                    </div>
                  </div>
                </div>
                <ul className="cruscotto-info-list">
                  <li><strong>Pre.</strong>: precodice associato all'articolo.</li>
                  <li><strong>Articolo</strong>: codice dell'articolo ordinato.</li>
                  <li>
                    <strong>Cliente</strong>: ragione sociale del cliente collegato
                    all'ordine.
                  </li>
                  <li><strong>Q.tà</strong>: quantità richiesta sull'ordine.</li>
                  <li><strong>Evasa</strong>: quantità già evasa.</li>
                  <li>
                    <strong>Da Ev.</strong>: quantità dell'ordine ancora da evadere.
                  </li>
                  <li>
                    <strong>Data / Numero / Tipo Ordine</strong>: riferimenti del
                    documento cliente.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cruscotto-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cruscotto-info-step">3</span>
                  <div>
                    <div className="fw-bold text-dark">Segnalazione stock in altri depositi</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Pallino rosso nella colonna “!”
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-start gap-3">
                  <span className="blink-dot mt-1 flex-shrink-0" />
                  <div style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
                    Il pallino rosso compare quando lo stesso articolo, con lo stesso
                    precodice e lo stesso numero ordine, presenta{" "}
                    <strong>giacenza maggiore di zero in almeno un deposito diverso</strong>{" "}
                    dal deposito {DEPOSITO_PRINCIPALE}.
                  </div>
                </div>
                <div
                  className="mt-3 p-3 rounded-3 bg-light border text-muted"
                  style={{ fontSize: "0.78rem", lineHeight: 1.5 }}
                >
                  Passando sul pallino viene mostrato l'elenco dei depositi in cui
                  risulta disponibile la giacenza. Cliccando la riga puoi verificare
                  il dettaglio completo.
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="cruscotto-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cruscotto-info-step">4</span>
                  <div>
                    <div className="fw-bold text-dark">Ordinamento e vista estesa</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Navigazione della tabella
                    </div>
                  </div>
                </div>
                <ul className="cruscotto-info-list">
                  <li>
                    Clicca sull'intestazione di una colonna per ordinare i dati in
                    modo crescente o decrescente.
                  </li>
                  <li>
                    Le colonne quantitative vengono ordinate numericamente; le
                    altre vengono ordinate come testo.
                  </li>
                  <li>
                    Clicca sul titolo del widget per aprire la{" "}
                    <strong>vista completa a schermo intero</strong>.
                  </li>
                  <li>
                    Il numero di <strong>RIGHE</strong> nell'header rappresenta le
                    righe attualmente presenti nella tabella del deposito principale.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          <div className="cruscotto-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="cruscotto-info-step">5</span>
              <div>
                <div className="fw-bold text-dark">Situazione analitica dei depositi</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Significato delle quantità nel dettaglio articolo
                </div>
              </div>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <ul className="cruscotto-info-list">
                  <li>
                    <strong>Giacenza a Magazzino</strong>: quantità fisicamente
                    presente nel deposito.
                  </li>
                  <li>
                    <strong>Impegnato</strong>: quantità già impegnata nei processi
                    gestionali.
                  </li>
                  <li>
                    <strong>Ordinato da Clienti</strong>: quantità associata agli
                    ordini cliente.
                  </li>
                  <li>
                    <strong>Disp. Attuale</strong>: disponibilità corrente risultante
                    per il deposito.
                  </li>
                </ul>
              </Col>
              <Col md={6}>
                <ul className="cruscotto-info-list">
                  <li>
                    <strong>Disp. Futura</strong>: disponibilità prevista considerando
                    i movimenti futuri presenti nei dati.
                  </li>
                  <li>
                    <strong>Q.tà Da Evadere</strong>: quantità ancora aperta
                    sull'ordine cliente selezionato.
                  </li>
                  <li>
                    <strong>Da Ordinare Fornitore</strong>: quantità che risulta da
                    approvvigionare lato fornitore.
                  </li>
                  <li>
                    Il deposito <strong>{DEPOSITO_PRINCIPALE}</strong> viene
                    evidenziato come <strong>Corrente</strong>; gli altri depositi
                    con giacenza disponibile vengono evidenziati separatamente.
                  </li>
                </ul>
              </Col>
            </Row>
          </div>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cruscotto-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cruscotto-info-step">6</span>
                  <div>
                    <div className="fw-bold text-dark">Dettaglio ordine cliente</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Riferimenti commerciali della richiesta
                    </div>
                  </div>
                </div>
                <ul className="cruscotto-info-list">
                  <li>Tipo, numero e data dell'ordine cliente.</li>
                  <li>Riferimento ordine cliente e anno di competenza.</li>
                  <li>Campo TC e quantità richiesta.</li>
                  <li>
                    Queste informazioni identificano il documento che genera il
                    fabbisogno visualizzato nel cruscotto.
                  </li>
                </ul>
              </div>
            </Col>

            <Col md={6}>
              <div className="cruscotto-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cruscotto-info-step">7</span>
                  <div>
                    <div className="fw-bold text-dark">Riferimenti fornitore</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Stato dell'approvvigionamento
                    </div>
                  </div>
                </div>
                <ul className="cruscotto-info-list">
                  <li>ID e ragione sociale del fornitore collegato.</li>
                  <li>Tipo, numero e data dell'ordine fornitore.</li>
                  <li>
                    <strong>Ord. Fornitore</strong>: quantità ordinata al fornitore.
                  </li>
                  <li>
                    <strong>Transito OFG</strong>: quantità riportata dal dato di
                    transito disponibile nel cruscotto.
                  </li>
                </ul>
              </div>
            </Col>
          </Row>

          <div className="cruscotto-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="cruscotto-info-step">8</span>
              <div>
                <div className="fw-bold text-dark">Anagrafica e tracciabilità</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Informazioni utili per risalire alla riga gestionale
                </div>
              </div>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <ul className="cruscotto-info-list">
                  <li>ID cliente e ragione sociale.</li>
                  <li>Operatore competente.</li>
                  <li>Data di inserimento della riga.</li>
                </ul>
              </Col>
              <Col md={6}>
                <ul className="cruscotto-info-list">
                  <li>ID ordine di sistema.</li>
                  <li>ID riga di sistema.</li>
                  <li>Contatore ordini associato al dato.</li>
                </ul>
              </Col>
            </Row>
          </div>

          <div className="cruscotto-info-card">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="cruscotto-info-step">9</span>
              <div>
                <div className="fw-bold text-dark">Note ordine e note riga</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Informazioni operative aggiuntive
                </div>
              </div>
            </div>
            <div style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
              Nel dettaglio vengono mantenute separate le{" "}
              <strong>Note Generali Ordine</strong>, riferite al documento nel suo
              complesso, e le <strong>Note Specifiche Riga</strong>, riferite invece
              all'articolo selezionato. Quando una nota non è presente viene
              visualizzata l'indicazione “Nessuna info”.
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-between">
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
            Suggerimento: usa il pallino rosso per individuare rapidamente articoli
            potenzialmente disponibili in un deposito alternativo.
          </div>
          <div className="d-flex gap-2">
            <Button variant="light" onClick={() => setShowInfoModal(false)}>
              Chiudi
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowInfoModal(false);
                setShowFullscreenModal(true);
              }}
            >
              Apri vista estesa
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* MODALE SCHERMO INTERO */}
      <Modal
        show={showFullscreenModal}
        onHide={() => setShowFullscreenModal(false)}
        fullscreen={true}
        animation={true}
      >
        <Modal.Body
          className="p-0 d-flex flex-column"
          style={{ background: "#f8f9fa" }}
        >
          <div
            className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm"
            style={{ zIndex: 110 }}
          >
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontWeight: "800",
                  fontSize: "0.9rem",
                  color: "#1a1a1a",
                  letterSpacing: "1px",
                }}
              >
                CRUSCOTTO WEB - DEPOSITO {DEPOSITO_PRINCIPALE} - COMPLETO
              </span>
              <button
                type="button"
                className="cruscotto-info-btn ms-2"
                onClick={() => setShowInfoModal(true)}
                aria-label="Informazioni sul Cruscotto Web"
              >
                <FiInfo size={16} />
              </button>
            </div>
            <Button
              variant="outline-dark"
              className="d-flex align-items-center gap-1 border-0"
              onClick={() => setShowFullscreenModal(false)}
            >
              <FiX size={16} /> Chiudi
            </Button>
          </div>
          <div className="flex-grow-1 p-4">
            <div className="bg-white shadow-sm rounded-4 border p-3">
              <RenderTabella isFullscreen={true} />
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {/* MODALE DETTAGLIO */}
      {/* MODALE DETTAGLIO */}
      <Modal
        show={!!selectedItem}
        onHide={() => setSelectedItem(null)}
        centered
        size="xl"
        contentClassName="border-0 shadow-lg rounded-4"
        className="p-5"
      >
        <Modal.Header
          closeButton
          className="border-bottom-0 bg-light rounded-top-4 p-4"
        >
          <Modal.Title className="d-flex align-items-center gap-2">
            <div className="bg-primary bg-opacity-10 p-2 rounded-3 text-primary d-flex align-items-center">
              <FiBox size={20} />
            </div>
            <div>
              <span
                className="text-muted small fw-medium d-block text-uppercase"
                style={{ letterSpacing: "0.5px" }}
              >
                Scheda Articolo - Deposito {DEPOSITO_PRINCIPALE}
              </span>
              <span className="fw-bold text-dark h5 mb-0">
                {selectedItem?.Articolo || "Nessuna info"}
              </span>
            </div>
            {haGiacenzaAltrove && (
              <span className="d-flex align-items-center gap-1 ms-2 text-danger small fw-bold">
                <span className="blink-dot" /> Disponibile altrove
              </span>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="p-4 bg-light bg-opacity-40"
          style={{ maxHeight: "80vh", overflowY: "auto" }}
        >
          {selectedItem && (
            <div>
              {/* Intestazione Principale Oggetto */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 bg-white border rounded-4 p-3 mb-4 shadow-sm">
                <div className="d-flex align-items-center">
                  <div className="bg-secondary bg-opacity-10 p-3 rounded-3 me-3 text-secondary">
                    <FiLayers size={24} />
                  </div>
                  <div>
                    <h4 className="fw-bold mb-1 text-dark h5">
                      {selectedItem.Descrizione ||
                        "Nessuna descrizione articolo"}
                    </h4>
                    <span className="text-muted small fw-semibold">
                      Precodice:{" "}
                      <span className="text-primary fw-bold">
                        {selectedItem.Prec || "Nessuna info"}
                      </span>{" "}
                      | Deposito Corrente:{" "}
                      <span className="text-dark fw-bold">
                        {selectedItem.DEP2 || "Nessuna info"}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="d-flex gap-3 align-items-center bg-light px-3 py-2 rounded-3">
                  <span className="text-muted small fw-bold">
                    ORDINE PRINCIPALE:
                  </span>
                  <span className="fw-bold text-dark">
                    {selectedItem["Tipo Ord"] || "Nessuna info"} /{" "}
                    {selectedItem["Num Ord"] || "Nessuna info"}
                  </span>
                </div>
              </div>

              {/* Banner Alert per Stock Altrove */}
              {haGiacenzaAltrove && (
                <div className="d-flex align-items-center gap-2 p-3 mb-3 rounded-4 border border-danger border-opacity-25 bg-danger bg-opacity-10 text-danger">
                  <FiAlertTriangle size={20} className="flex-shrink-0" />
                  <div className="small fw-semibold">
                    Attenzione: questo articolo risulta a stock in altri
                    depositi (
                    {depositiConGiacenzaAltrove.map((d) => d.DEP2).join(", ")}).
                  </div>
                </div>
              )}

              {/* SEZIONE 1: Situazione Analitica Depositi */}
              <h6
                className="text-uppercase text-secondary fw-bold mb-2"
                style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
              >
                Situazione Analitica su tutti i depositi
              </h6>
              <div className="bg-white border rounded-4 p-3 mb-4 shadow-sm">
                <Table
                  responsive
                  hover
                  bordered
                  className="align-middle mb-0 text-center"
                  style={{ fontSize: "0.8rem" }}
                >
                  <thead className="table-light fw-bold text-secondary">
                    <tr>
                      <th>Dep.</th>
                      <th>Giacenza a Magazzino</th>
                      <th>Impegnato</th>
                      <th>Ordinato da Clienti</th>
                      <th>Disp. Attuale</th>
                      <th>Disp. Futura</th>
                      <th>Q.tà Da Evadere (Su Ordine)</th>
                      <th>Da Ordinare Fornitore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantiArticoloSelezionato.map((v, idx) => {
                      const isPrincipale = v.DEP2 === DEPOSITO_PRINCIPALE;
                      const giacenzaDisponibileAltrove =
                        !isPrincipale && formatQta(v.Giacenza2) > 0;
                      return (
                        <tr
                          key={idx}
                          className={
                            isPrincipale
                              ? "table-primary fw-semibold"
                              : giacenzaDisponibileAltrove
                                ? "dep-row-alert"
                                : ""
                          }
                        >
                          <td className="fw-bold">
                            {v.DEP2 || "Nessuna info"}
                            {isPrincipale && (
                              <span
                                className="badge bg-primary ms-1"
                                style={{ fontSize: "0.6rem" }}
                              >
                                Corrente
                              </span>
                            )}
                          </td>
                          <td
                            className={
                              giacenzaDisponibileAltrove
                                ? "text-danger fw-bold"
                                : "text-dark"
                            }
                          >
                            {formatQta(v.Giacenza2)}
                          </td>
                          <td className="text-muted">
                            {formatQta(v["Imp.2"])}
                          </td>
                          <td className="text-muted">
                            {formatQta(v["Ord.2"])}
                          </td>
                          <td
                            className={
                              formatQta(v["Disp. Attuale2"]) < 0
                                ? "text-danger fw-semibold"
                                : "text-success fw-semibold"
                            }
                          >
                            {formatQta(v["Disp. Attuale2"])}
                          </td>
                          <td
                            className={
                              formatQta(v["Disp. Futura2"]) < 0
                                ? "text-danger fw-semibold"
                                : "text-success fw-semibold"
                            }
                          >
                            {formatQta(v["Disp. Futura2"])}
                          </td>
                          <td className="text-danger fw-bold">
                            {formatQta(v["Qta Da Evadere"])}
                          </td>
                          <td className="text-primary fw-bold">
                            {formatQta(v["Qta Da Ordinare Fornitori"])}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Griglia Dettagli Completa */}
              <Row className="g-3 mb-4">
                {/* SEZIONE DATO ORDINE CLIENTE */}
                <Col xs={12} md={4}>
                  <div className="bg-white border rounded-4 p-3 h-100 shadow-sm">
                    <span
                      className="text-muted text-uppercase fw-bold d-block mb-3"
                      style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}
                    >
                      Dettagli Ordine Cliente
                    </span>
                    <ul
                      className="list-unstyled mb-0 style-modal-list"
                      style={{ fontSize: "0.8rem" }}
                    >
                      <li className="mb-2">
                        <strong>Tipo Ordine:</strong>{" "}
                        {selectedItem["Tipo Ord"] || (
                          <span className="text-muted fst-italic">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-2">
                        <strong>Numero Ordine:</strong>{" "}
                        {selectedItem["Num Ord"] || (
                          <span className="text-muted fst-italic">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-2">
                        <strong>Data Ordine:</strong>{" "}
                        {formatData(selectedItem["Data Ord"])}
                      </li>
                      <li className="mb-2">
                        <strong>Rif. Ordine Cliente:</strong>{" "}
                        {selectedItem["Rif Ord Cli"] || (
                          <span className="text-muted fst-italic">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-2">
                        <strong>Anno Competenza:</strong>{" "}
                        {selectedItem["Anno"] || (
                          <span className="text-muted fst-italic">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-2">
                        <strong>TC:</strong>{" "}
                        {selectedItem["TC"] || (
                          <span className="text-muted fst-italic">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-0">
                        <strong>Q.tà Richiesta:</strong>{" "}
                        <span className="fw-bold">
                          {formatQta(selectedItem["Qta"])}
                        </span>
                      </li>
                    </ul>
                  </div>
                </Col>

                {/* SEZIONE RIFERIMENTI FORNITORE */}
                <Col xs={12} md={4}>
                  <div className="bg-white border rounded-4 p-3 h-100 shadow-sm">
                    <span
                      className="text-muted text-uppercase fw-bold d-block mb-3"
                      style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}
                    >
                      Riferimenti Fornitore
                    </span>
                    <ul
                      className="list-unstyled mb-0 style-modal-list"
                      style={{ fontSize: "0.8rem" }}
                    >
                      <li className="mb-2">
                        <strong>ID Fornitore:</strong>{" "}
                        {selectedItem["ID Fornitore"] || (
                          <span className="badge bg-secondary bg-opacity-10 text-secondary border">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-2">
                        <strong>Rag. Soc. Fornitore:</strong>{" "}
                        {selectedItem["Rag Soc Fornitore"] || (
                          <span className="text-muted fst-italic">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-2">
                        <strong>Tipo Ord. Fornitore:</strong>{" "}
                        {selectedItem["Tipo Ord For"] || (
                          <span className="text-muted fst-italic">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-2">
                        <strong>Num. Ord. Fornitore:</strong>{" "}
                        {selectedItem["Num Ord For"] || (
                          <span className="text-muted fst-italic">
                            Nessuna info
                          </span>
                        )}
                      </li>
                      <li className="mb-2">
                        <strong>Data Ord. Fornitore:</strong>{" "}
                        {formatData(selectedItem["Data Ord For"])}
                      </li>
                      <li className="mb-2">
                        <strong>Ord. Fornitore (Q.tà):</strong>{" "}
                        <span className="fw-bold text-primary">
                          {formatQta(selectedItem["Ord Forn"])}
                        </span>
                      </li>
                      <li className="mb-0">
                        <strong>Transito OFG:</strong>{" "}
                        <span className="fw-bold text-warning">
                          {formatQta(selectedItem["Transito OFG"])}
                        </span>
                      </li>
                    </ul>
                  </div>
                </Col>

                {/* SEZIONE ANAGRAFICA CLIENTE E TRACCIABILITÀ */}
                <Col xs={12} md={4}>
                  <div className="bg-white border rounded-4 p-3 h-100 shadow-sm d-flex flex-column justify-content-between">
                    <div>
                      <span
                        className="text-muted text-uppercase fw-bold d-block mb-3"
                        style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}
                      >
                        Anagrafica & Tracciabilità
                      </span>
                      <ul
                        className="list-unstyled mb-3 style-modal-list"
                        style={{ fontSize: "0.8rem" }}
                      >
                        <li className="mb-2">
                          <strong>ID Cliente:</strong>{" "}
                          {selectedItem["ID Cliente"] || "Nessuna info"}
                        </li>
                        <li className="mb-2">
                          <strong>Ragione Sociale:</strong>{" "}
                          <span className="text-dark fw-semibold">
                            {selectedItem["Ragione Sociale"] || "Nessuna info"}
                          </span>
                        </li>
                        <li className="mb-2">
                          <strong>Operatore Competente:</strong>{" "}
                          {selectedItem["Operatore"] || (
                            <span className="text-muted fst-italic">
                              Nessuna info
                            </span>
                          )}
                        </li>
                        <li className="mb-0">
                          <strong>Data Inserimento Riga:</strong>{" "}
                          {formatData(selectedItem["Data Inserimento Riga"])}
                        </li>
                      </ul>
                    </div>
                    <div
                      className="pt-2 border-top"
                      style={{ fontSize: "0.75rem" }}
                    >
                      <span className="text-muted d-block">
                        ID Ordine Sistema:{" "}
                        <strong className="text-dark">
                          {selectedItem["id_ordine"] || "-"}
                        </strong>
                      </span>
                      <span className="text-muted d-block">
                        ID Riga Sistema:{" "}
                        <strong className="text-dark">
                          {selectedItem["id_riga"] || "-"}
                        </strong>
                      </span>
                      <span className="text-muted d-block">
                        Contatore Ordini:{" "}
                        <strong className="text-dark">
                          {selectedItem["count_ord"] || "0"}
                        </strong>
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* AREA NOTE DI TESTA ED NOTE DI RIGA */}
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <div
                    className={`p-3 rounded-4 h-100 border ${selectedItem["Note"] ? "bg-warning bg-opacity-10 text-warning-emphasis border-warning border-opacity-25" : "bg-white text-muted"}`}
                  >
                    <FiClipboard className="flex-shrink-0 me-2" size={16} />
                    <span
                      className="small fw-bold text-uppercase"
                      style={{ fontSize: "0.65rem" }}
                    >
                      Note Generali Ordine:
                    </span>
                    <div className="mt-2 small">
                      {selectedItem["Note"] ? (
                        selectedItem["Note"]
                      ) : (
                        <span className="fst-italic">Nessuna info</span>
                      )}
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div
                    className={`p-3 rounded-4 h-100 border ${selectedItem["Note Riga"] ? "bg-primary bg-opacity-10 text-primary border-primary border-opacity-25" : "bg-white text-muted"}`}
                  >
                    <FiClipboard className="flex-shrink-0 me-2" size={16} />
                    <span
                      className="small fw-bold text-uppercase"
                      style={{ fontSize: "0.65rem" }}
                    >
                      Note Specifiche Riga:
                    </span>
                    <div className="mt-2 small">
                      {selectedItem["Note Riga"] ? (
                        selectedItem["Note Riga"]
                      ) : (
                        <span className="fst-italic">Nessuna info</span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top-0 bg-light rounded-bottom-4 p-3">
          <Button
            variant="secondary"
            className="px-4 rounded-3 border-0"
            onClick={() => setSelectedItem(null)}
          >
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>{" "}
    </Card>
  );
};

export default CruscottoWebTabella;
